import { AxiosResponse } from 'axios'
import {
  AdminCareTeamsRequest,
  ApiSuccessResponse,
  BaseTableRequest,
  EOp,
  InviteSentVia,
  OrganizationalUser,
  ReportRequestWithDate,
  RequestById,
  RequestUpdateSharedItemStatus,
  ShareTimelineItem,
  SharingTimelineRequest,
  TableResponse,
  Team,
  TeamCreateInOrganizationRequest,
  TeamCreateRequest,
  TeamInfo,
  TeamMember,
  TeamMemberCreateRequest,
  TeamMemberRemoveRequest,
  TeamMembersRequest,
  TeamMemberUpdateRequest,
  TeamShareRequest,
  TeamUpdateRequest,
} from 'types'
import axiosInstance from './axios'

export const getTeams = async () => {
  return axiosInstance.post<void, TableResponse<Team>>('/v2/care-teams')
}

export const getTeamMembers = async (data: TeamMembersRequest) => {
  return axiosInstance.post<TeamMembersRequest, TableResponse<TeamMember>>(
    '/v2/care-teams/members',
    data,
  )
}

export const createTeam = async (data: TeamCreateRequest) => {
  return axiosInstance.post<TeamCreateRequest, AxiosResponse<Team>>('/v2/care-teams/create', data)
}

export const updateTeam = async (data: TeamUpdateRequest) => {
  return axiosInstance.post<TeamUpdateRequest, AxiosResponse<Team>>('/v2/care-teams/update', data)
}

export const createTeamMember = async (data: TeamMemberCreateRequest) => {
  return axiosInstance.post<TeamMemberCreateRequest, AxiosResponse<TeamMember>>(
    '/v2/care-teams/add-member',
    data,
  )
}

/** Success message for add-member, including the invitation channel reported by the backend. */
export const teamMemberCreatedMessage = (inviteSentVia?: InviteSentVia): string => {
  const base = 'Team Member successfully created'
  switch (inviteSentVia) {
    case 'email':
      return `${base}. Invitation sent via email`
    case 'sms':
      return `${base}. Invitation sent via text message`
    case 'both':
      return `${base}. Invitation sent via email and text message`
    default:
      return base
  }
}

export const updateTeamMember = async (data: TeamMemberUpdateRequest) => {
  return axiosInstance.post<TeamMemberUpdateRequest, AxiosResponse<TeamMember>>(
    '/v2/care-teams/update-member',
    data,
  )
}

export const removeTeam = async (data: RequestById) => {
  return axiosInstance.post<RequestById, ApiSuccessResponse>('/v2/care-teams/delete', data)
}

export const removeTeamMember = async (data: TeamMemberRemoveRequest) => {
  return axiosInstance.post<TeamMemberRemoveRequest, ApiSuccessResponse>(
    '/v2/care-teams/delete-member',
    data,
  )
}

export const getTeamsTable = async (request: ReportRequestWithDate | AdminCareTeamsRequest) => {
  return axiosInstance.post<void, TableResponse<TeamInfo>>('/admin/care-teams', request)
}

export const getOrgTeamsTable = async (request: ReportRequestWithDate) => {
  return axiosInstance.post<void, TableResponse<TeamInfo>>('/organizations/care-teams', request)
}

export const shareTeamContent = async (data: TeamShareRequest) => {
  return axiosInstance.post<TeamShareRequest, AxiosResponse<ApiSuccessResponse>>(
    '/content-shares/share',
    data,
  )
}

export const getSharedTimeline = async (request: SharingTimelineRequest) => {
  return axiosInstance.post<BaseTableRequest, AxiosResponse<ShareTimelineItem[]>>(
    '/content-shares',
    {
      filter: {
        userId: {
          type: 'number',
          op: EOp.equals,
          value: request.userId,
        },
        careTeamId: {
          type: 'number',
          op: EOp.equals,
          value: request.teamId,
        },
      },
    },
  )
}

export const getSharedSentTimeline = async (request: SharingTimelineRequest) => {
  return axiosInstance.post<BaseTableRequest, AxiosResponse<ShareTimelineItem[]>>(
    '/content-shares',
    {
      filter: {
        senderId: {
          type: 'number',
          op: EOp.equals,
          value: request.userId,
        },
        careTeamId: {
          type: 'number',
          op: EOp.equals,
          value: request.teamId,
        },
      },
    },
  )
}

export const readSharedArticle = async (request: RequestById) => {
  return axiosInstance.post<RequestById, AxiosResponse<ShareTimelineItem>>(
    '/content-shares/read',
    request,
  )
}

export const setStatusSharedItem = async (
  request: RequestUpdateSharedItemStatus,
): Promise<AxiosResponse<ShareTimelineItem>> => {
  return axiosInstance.post('/content-shares/status', request)
}

export const createTeamInOrg = async (
  request: TeamCreateInOrganizationRequest,
): Promise<TableResponse<OrganizationalUser[]>> => {
  return axiosInstance.post('/organizations/care-teams/create', request)
}

export const createTeamInAdmin = async (
  request: TeamCreateInOrganizationRequest,
): Promise<TableResponse<OrganizationalUser[]>> => {
  return axiosInstance.post('/admin/care-teams/create', request)
}
